import React, { forwardRef, useCallback, useEffect } from 'react';
import { VariableSizeGrid as Grid } from 'react-window';
import GridItem from '../LayoutItems/GridItem';
import { normalizeIndex, findSectionForRow, getGridItemIndex, warnOnNoSection } from '../shared';
import withMemoizedLayout from './withMemoizedLayout';
import useSetLayoutRef from './useSetLayoutRef';

export const getGridItemKey = (data, sections, cols) => {
  const { rowIndex, columnIndex } = data,
    section = findSectionForRow(sections, rowIndex),
    fallbackKey = `${rowIndex}:${columnIndex}`;

  let key;

  if (!section) {
    warnOnNoSection(fallbackKey, 'grid');
  }

  if (section && section.itemKey) {
    const index = getGridItemIndex(columnIndex, rowIndex, cols);

    if (index < section.bufferStartIndex) {
      const relIndex = normalizeIndex(index, section);
      key = section.itemKey(relIndex, { ...data.data, ...section.itemProps });
    }
  }

  return key || fallbackKey;
};

export const GridLayout = forwardRef((props, ref) => {
  const { rows, cols, sections, itemWidth, ...gridProps } = props;

  const { layout, setLayoutRef } = useSetLayoutRef(ref);

  useEffect(() => {
    layout?.resetAfterIndices({
      columnIndex: 0,
      rowIndex: 0,
      shouldForceUpdate: true,
    });
  }, [itemWidth, layout]);

  const getColWidth = useCallback(() => {
    return itemWidth;
  }, [itemWidth]);

  const getItemHeight = useCallback(
    (index) => {
      const section = findSectionForRow(sections, index);
      return section ? section.itemHeight : 0;
    },
    [sections]
  );

  const getItemKey = useCallback(
    (data) => {
      return getGridItemKey(data, sections, cols);
    },
    [sections, cols]
  );

  useEffect(() => {
    if (layout) {
      layout.resetAfterRowIndex(0);
    }
  });

  return (
    <Grid
      {...gridProps}
      ref={setLayoutRef}
      columnCount={cols}
      columnWidth={getColWidth}
      rowHeight={getItemHeight}
      rowCount={rows}
      itemKey={getItemKey}
      itemData={{
        sections,
        cols,
      }}
    >
      {GridItem}
    </Grid>
  );
});

export default withMemoizedLayout(GridLayout);
